/**
 * 2-step category jump tabs (Netlify .cat-nav parity):
 * - pin under header on desk + mobile (fixed; Shopify sections are too short for sticky)
 * - active tab rotates to first place (FLIP slide)
 * - active accent syncs underline, promo text, and offer pills
 * - offer pills crossfade on a timer
 */
class MgTwoStepTabs extends HTMLElement {
  /** @type {boolean} */
  #bound = false;

  /** @type {number | undefined} */
  #offerTimer;

  /** @type {boolean} */
  #scrollingToTab = false;

  /** @type {number | undefined} */
  #scrollUnlockTimer;

  /** @type {boolean} */
  #scrollTicking = false;

  /** @type {string | undefined} */
  #activeId;

  /** @type {Element | null} */
  #scrollRoot = null;

  /** @type {Set<EventTarget>} */
  #scrollTargets = new Set();

  connectedCallback() {
    if (this.#bound) return;
    this.#bound = true;

    this.addEventListener('click', this.#onClick);
    document.addEventListener('visibilitychange', this.#onVisibilityChange);
    window.addEventListener('resize', this.#onResize);
    this.#bindScrollTargets();

    // Clear any stale inline spacer height left by editor morph / prior layout thrash
    const spacer = this.#sectionSpacer();
    if (spacer) {
      spacer.style.height = '';
      spacer.classList.remove('is-active');
    }
    this.classList.remove('is-pinned');

    this.#syncFromScroll({ force: true });
    this.#updatePin();
    this.#publishTabsOffset();
    this.#startOfferRotation();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
    document.removeEventListener('visibilitychange', this.#onVisibilityChange);
    window.removeEventListener('resize', this.#onResize);
    this.#unbindScrollTargets();
    this.#stopOfferRotation();
    if (this.#scrollUnlockTimer) window.clearTimeout(this.#scrollUnlockTimer);
  }

  get #offerInterval() {
    const value = Number(this.dataset.offerInterval);
    return Number.isFinite(value) && value >= 500 ? value : 2600;
  }

  get #reducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  /** Prefer theme scroll container (.page-wrapper) over window. */
  #findScrollRoot() {
    const pageWrapper = document.querySelector('.page-wrapper');
    if (pageWrapper instanceof HTMLElement) {
      const style = getComputedStyle(pageWrapper);
      if (style.overflowY === 'auto' || style.overflowY === 'scroll' || style.overflowY === 'overlay') {
        return pageWrapper;
      }
    }

    let el = this.parentElement;
    while (el && el !== document.body) {
      const style = getComputedStyle(el);
      const overflowY = style.overflowY;
      if (overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'overlay') {
        return el;
      }
      el = el.parentElement;
    }
    return document.scrollingElement;
  }

  #bindScrollTargets() {
    this.#unbindScrollTargets();
    this.#scrollRoot = this.#findScrollRoot();

    const targets = [window, document, this.#scrollRoot, document.querySelector('.page-wrapper')];
    for (const target of targets) {
      if (!target || this.#scrollTargets.has(target)) continue;
      target.addEventListener('scroll', this.#onScroll, { passive: true });
      this.#scrollTargets.add(target);
    }
  }

  #unbindScrollTargets() {
    this.#scrollTargets.forEach((target) => {
      target.removeEventListener('scroll', this.#onScroll);
    });
    this.#scrollTargets.clear();
  }

  #onResize = () => {
    this.#bindScrollTargets();
    this.#updatePin({ forceMeasure: true });
    this.#publishTabsOffset();
  };

  #headerOffset() {
    if (!this.#isHeaderSticky()) return 0;

    const sources = [
      getComputedStyle(document.documentElement).getPropertyValue('--header-height'),
      getComputedStyle(document.body).getPropertyValue('--header-height'),
      document.body.style.getPropertyValue('--header-height'),
      document.documentElement.style.getPropertyValue('--header-height'),
    ];

    for (const raw of sources) {
      const value = Number.parseFloat(String(raw).trim());
      if (Number.isFinite(value) && value >= 0) return value;
    }
    return 0;
  }

  /** Match theme sticky header rules: always, or scroll-up while active. */
  #isHeaderSticky() {
    const header = document.getElementById('header-component');
    if (!(header instanceof HTMLElement)) return false;

    const mode = header.getAttribute('sticky');
    if (mode === 'always') return true;
    if (mode === 'scroll-up') return header.dataset.stickyState === 'active';
    return false;
  }

  /** @returns {HTMLAnchorElement[]} */
  #tabs() {
    return Array.from(this.querySelectorAll('[data-mg-tst-tab]')).filter(
      (el) => el instanceof HTMLAnchorElement
    );
  }

  /** @returns {string[]} */
  #tabIds() {
    return this.#tabs()
      .map((tab) => (tab.dataset.targetId || '').trim())
      .filter((id) => id !== '');
  }

  /** @returns {HTMLElement | null} */
  #sectionSpacer() {
    const section = this.closest('.mg-two-step-tabs-section') || this.parentElement;
    const el = section?.querySelector('[data-mg-tst-section-spacer]');
    return el instanceof HTMLElement ? el : null;
  }

  #onScroll = () => {
    if (this.#scrollTicking) return;
    this.#scrollTicking = true;
    requestAnimationFrame(() => {
      this.#updatePin();
      if (!this.#scrollingToTab) this.#syncFromScroll();
      this.#scrollTicking = false;
    });
  };

  #onVisibilityChange = () => {
    if (document.hidden) {
      this.#stopOfferRotation();
    } else {
      this.#startOfferRotation();
    }
  };

  /** @param {Event} event */
  #onClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const tab = target.closest('[data-mg-tst-tab]');
    if (!(tab instanceof HTMLAnchorElement) || !this.contains(tab)) return;

    const id = (tab.dataset.targetId || '').trim();
    if (!id) return;

    const destination = document.getElementById(id);
    if (!destination) return;

    event.preventDefault();
    this.#setActiveTab(tab, { animate: true });
    this.#scrollingToTab = true;
    if (this.#scrollUnlockTimer) window.clearTimeout(this.#scrollUnlockTimer);

    this.#scrollToPair(destination);

    try {
      history.replaceState(null, '', `#${id}`);
    } catch {
      /* ignore */
    }

    this.#scrollUnlockTimer = window.setTimeout(() => {
      this.#scrollingToTab = false;
      this.#syncFromScroll();
    }, 1000);
  };

  /**
   * Scroll the theme scroll root to a pair, accounting for sticky header + tabs.
   * Native scrollIntoView often misses .page-wrapper and ignores our pin offset.
   * @param {HTMLElement} destination
   */
  #scrollToPair(destination) {
    const root = this.#scrollRoot instanceof HTMLElement ? this.#scrollRoot : this.#findScrollRoot();
    const behavior = this.#reducedMotion ? 'auto' : 'smooth';
    const offset = this.#headerOffset() + this.#measurePinHeight();

    if (root instanceof HTMLElement && root !== document.documentElement && root !== document.body) {
      const rootTop = root.getBoundingClientRect().top;
      const destTop = destination.getBoundingClientRect().top;
      const top = Math.max(0, root.scrollTop + destTop - rootTop - offset);
      root.scrollTo({ top, behavior });
      return;
    }

    const top = Math.max(0, window.scrollY + destination.getBoundingClientRect().top - offset);
    window.scrollTo({ top, behavior });
  }

  /**
   * Active section = last pair whose top has crossed the pinned nav bottom.
   * @param {{ force?: boolean }} [options]
   */
  #syncFromScroll(options = {}) {
    const ids = this.#tabIds();
    if (ids.length === 0) return;

    const threshold = this.classList.contains('is-pinned')
      ? this.#headerOffset() + this.#measurePinHeight() + 4
      : this.getBoundingClientRect().bottom + 4;
    let current = ids[0];
    if (!current) return;

    for (const id of ids) {
      const el = document.getElementById(id);
      if (!el) continue;
      if (el.getBoundingClientRect().top <= threshold) current = id;
    }

    if (!current) return;
    if (!options.force && current === this.#activeId) return;

    const match = this.querySelector(`[data-mg-tst-tab][data-target-id="${CSS.escape(current)}"]`);
    if (match instanceof HTMLAnchorElement) {
      this.#setActiveTab(match, { animate: !options.force });
    }
  }

  /**
   * @param {HTMLAnchorElement} activeTab
   * @param {{ animate?: boolean }} [options]
   */
  #setActiveTab(activeTab, options = {}) {
    const id = (activeTab.dataset.targetId || '').trim();
    const animate = Boolean(options.animate) && !this.#reducedMotion;
    const isSame = id !== '' && id === this.#activeId;

    this.#tabs().forEach((tab) => {
      const isActive = tab === activeTab;
      tab.classList.toggle('is-active', isActive);
      if (isActive) {
        tab.setAttribute('aria-current', 'true');
      } else {
        tab.removeAttribute('aria-current');
      }
    });

    const accent = (activeTab.dataset.accent || '').trim();
    if (accent) {
      this.style.setProperty('--mg-tst-active', accent);
    } else {
      this.style.removeProperty('--mg-tst-active');
    }

    this.querySelectorAll('[data-mg-tst-offer]').forEach((offer) => {
      if (!(offer instanceof HTMLElement)) return;
      if (offer.dataset.lockBadgeBg === 'true') return;
      if (accent) {
        offer.style.setProperty('--mg-tst-badge-bg', accent);
      } else {
        offer.style.removeProperty('--mg-tst-badge-bg');
      }
    });

    if (!isSame) {
      this.#reorderTabs(activeTab, { animate });
      this.#activeId = id || undefined;
    }
  }

  /**
   * Rotate tabs so the active one leads; "+" stay in odd flex slots.
   * FLIP: only titles moving left slide; wrap-around snaps.
   * @param {HTMLAnchorElement} activeTab
   * @param {{ animate?: boolean }} [options]
   */
  #reorderTabs(activeTab, options = {}) {
    const tabs = this.#tabs();
    if (tabs.length < 2) return;

    const ids = tabs.map((tab) => (tab.dataset.targetId || '').trim()).filter(Boolean);
    const current = (activeTab.dataset.targetId || '').trim();
    const startIndex = Math.max(0, ids.indexOf(current));
    const order = ids.slice(startIndex).concat(ids.slice(0, startIndex));

    /** @type {Record<string, DOMRect>} */
    const firstRects = {};
    if (options.animate) {
      tabs.forEach((tab) => {
        const key = tab.dataset.targetId || '';
        if (key) firstRects[key] = tab.getBoundingClientRect();
      });
    }

    order.forEach((tabId, index) => {
      const tab = this.querySelector(`[data-mg-tst-tab][data-target-id="${CSS.escape(tabId)}"]`);
      if (tab instanceof HTMLElement) {
        tab.style.order = String(index * 2);
      }
    });

    if (!options.animate) return;

    tabs.forEach((tab) => {
      const key = tab.dataset.targetId || '';
      const first = firstRects[key];
      if (!first) return;

      const last = tab.getBoundingClientRect();
      const dx = first.left - last.left;
      if (dx <= 0) return;

      tab.style.transition = 'none';
      tab.style.transform = `translateX(${dx}px)`;
      void tab.offsetWidth;

      requestAnimationFrame(() => {
        tab.style.transition = '';
        tab.style.transform = '';
      });
    });
  }

  /**
   * Pin bar height from the nav only (never the host/section).
   * Clamped so breakpoint/editor resize thrash cannot inflate the flow spacer
   * into a full-viewport white gap under the fixed tabs.
   * @returns {number}
   */
  #measurePinHeight() {
    const nav = this.querySelector('.mg-two-step-tabs__nav');
    const raw =
      (nav instanceof HTMLElement ? nav.offsetHeight : 0) || this.offsetHeight || 72;
    return Math.min(Math.max(Math.round(raw), 40), 200);
  }

  /**
   * Pin the whole section under the header (Shopify section height is too short for sticky).
   * @param {{ forceMeasure?: boolean }} [options]
   */
  #updatePin(options = {}) {
    const spacer = this.#sectionSpacer();
    if (!spacer) return;

    const headerOffset = this.#headerOffset();
    const isPinned = this.classList.contains('is-pinned');

    const shouldPin = isPinned
      ? spacer.getBoundingClientRect().top <= headerOffset
      : this.getBoundingClientRect().top <= headerOffset;

    if (shouldPin && (!isPinned || options.forceMeasure)) {
      const height = this.#measurePinHeight();
      spacer.style.height = `${height}px`;
      spacer.classList.add('is-active');
      this.classList.add('is-pinned');
    } else if (!shouldPin && isPinned) {
      this.classList.remove('is-pinned');
      spacer.classList.remove('is-active');
      spacer.style.height = '';
    } else if (isPinned && options.forceMeasure) {
      spacer.style.height = `${this.#measurePinHeight()}px`;
    }

    this.#publishTabsOffset();
  }

  /** Expose pinned bar height so pair sections can offset scroll-margin. */
  #publishTabsOffset() {
    const tabsHeight = this.#measurePinHeight();
    const scrollMargin = this.#headerOffset() + tabsHeight;
    document.documentElement.style.setProperty('--mg-tsp-tabs-offset', `${tabsHeight}px`);
    document.documentElement.style.setProperty('--mg-tsp-scroll-margin', `${scrollMargin}px`);
  }

  #startOfferRotation() {
    this.#stopOfferRotation();

    const offersRoot = this.querySelector('[data-mg-tst-offers]');
    if (!(offersRoot instanceof HTMLElement) || offersRoot.dataset.single === 'true') return;

    const offers = Array.from(offersRoot.querySelectorAll('[data-mg-tst-offer]'));
    if (offers.length < 2 || this.#reducedMotion) return;

    let index = Math.max(
      0,
      offers.findIndex((offer) => offer.classList.contains('is-active'))
    );

    this.#offerTimer = window.setInterval(() => {
      const current = offers[index];
      current?.classList.remove('is-active');
      current?.setAttribute('aria-hidden', 'true');

      index = (index + 1) % offers.length;
      const next = offers[index];
      next?.classList.add('is-active');
      next?.setAttribute('aria-hidden', 'false');
    }, this.#offerInterval);
  }

  #stopOfferRotation() {
    if (this.#offerTimer) {
      window.clearInterval(this.#offerTimer);
      this.#offerTimer = undefined;
    }
  }
}

if (!customElements.get('mg-two-step-tabs')) {
  customElements.define('mg-two-step-tabs', MgTwoStepTabs);
}
